import { useEffect, useState } from "react";
import { RotateCcw, X } from "lucide-react";
import { toast } from "sonner";

const DRAFT_KEY = "rentalmnp:simulation:draft";

interface DraftMeta {
  email?: string;
  savedAt?: number;
}

function readDraft(): DraftMeta | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DraftMeta;
    return parsed ?? null;
  } catch {
    return null;
  }
}

function formatRelative(ts?: number): string {
  if (!ts) return "";
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days} j`;
}

interface Props {
  onResume: () => void;
}

export function ResumeBanner({ onResume }: Props) {
  const [draft, setDraft] = useState<DraftMeta | null>(null);

  useEffect(() => {
    setDraft(readDraft());
  }, []);

  if (!draft) return null;

  const clear = () => {
    try {
      window.localStorage.removeItem(DRAFT_KEY);
    } catch {
      /* ignore */
    }
    setDraft(null);
    toast.success("Brouillon effacé", { duration: 1500 });
  };

  return (
    <div className="border-b border-primary/20 bg-primary/5">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-6 py-2.5 text-sm lg:px-12">
        <div className="flex min-w-0 items-center gap-2 text-foreground">
          <RotateCcw className="h-4 w-4 shrink-0 text-primary" />
          <span className="truncate">
            Vous avez une simulation en cours
            {draft.savedAt && (
              <span className="text-muted-foreground"> · {formatRelative(draft.savedAt)}</span>
            )}
            {draft.email && (
              <span className="hidden text-muted-foreground sm:inline"> · {draft.email}</span>
            )}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onResume}
            className="rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow-sm transition hover:bg-primary-dark"
          >
            Reprendre ma simulation
          </button>
          <button
            type="button"
            onClick={clear}
            aria-label="Effacer le brouillon"
            className="rounded-md p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
